import React, { useState, useEffect } from "react";
import { Phone, Wifi, Gamepad2, CreditCard, Clock } from "lucide-react";
import { fetchTransactionHistory } from "../../services/topupService";
import moment from "moment";
import dayjs from "dayjs";
import { DatePicker, Button, Form, Pagination, Tabs, Input } from "antd";
import locale from "antd/es/date-picker/locale/vi_VN";
import "dayjs/locale/vi";
import { useLocation } from "react-router-dom";

dayjs.locale("vi");

const PAGE_SIZE = 10;

const getServiceInfo = (item) => {
  if (item.orderphonegames?.length > 0) {
    return { type: "game", label: "Nạp game", icon: Gamepad2, bg: "bg-orange-100", color: "text-orange-600", servicecode: item.orderphonegames[0].servicecode };
  }
  if (item.orderphonetopups?.length > 0) {
    return { type: "topup", label: "Nạp tiền điện thoại", icon: Phone, bg: "bg-blue-100", color: "text-blue-600", servicecode: item.orderphonetopups[0].servicecode };
  }
  if (item.orderphonedatapacks?.length > 0) {
    return { type: "data", label: "Mua gói data", icon: Wifi, bg: "bg-teal-100", color: "text-teal-600", servicecode: item.orderphonedatapacks[0].servicecode };
  }
  if (item.orderphonecards?.length > 0) {
    return { type: "card", label: "Mua thẻ", icon: CreditCard, bg: "bg-cyan-100", color: "text-cyan-600", servicecode: item.orderphonecards[0].servicecode };
  }
  return { type: "other", label: "Dịch vụ", icon: Phone, bg: "bg-blue-100", color: "text-blue-600", servicecode: null };
};

const History = ({ setLoading, loading }) => {
  const [form] = Form.useForm();
  const [listHistory, setListHistory] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState("1");
  const [hasSearched, setHasSearched] = useState(true);
  const location = useLocation();

  const rangePresets = [
    { label: "7 ngày qua", value: [dayjs().add(-7, "d"), dayjs()] },
    { label: "14 ngày qua", value: [dayjs().add(-14, "d"), dayjs()] },
    { label: "30 ngày qua", value: [dayjs().add(-30, "d"), dayjs()] },
    { label: "90 ngày qua", value: [dayjs().add(-90, "d"), dayjs()] },
  ];

  const formatVND = (amount) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(amount);
  };

  const getListHistory = async (page, dateRangeVal, orderCode = null) => {
    const targetPage = orderCode ? 1 : page;
    let startTime = "";
    let endTime = "";

    if (!orderCode && dateRangeVal && dateRangeVal.length === 2) {
      const [start, end] = dateRangeVal;
      startTime = start.startOf("day").format("DD/MM/YYYY HH:mm:ss");
      endTime = end.endOf("day").format("DD/MM/YYYY HH:mm:ss");
    }

    setLoading(true);
    try {
      const res = await fetchTransactionHistory(startTime, endTime, targetPage, PAGE_SIZE, orderCode);
      const data = res?.data?.data?.data || [];
      setListHistory(Array.isArray(data) ? data : []);
      setTotal(res?.data?.data?.totalRecordCount || res?.data?.data?.totalrecordcount || 0);
    } catch (error) {
      console.error("History error:", error);
      setListHistory([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const orderCode = queryParams.get("orderCode");
    const tab = queryParams.get("tab");

    if (tab) {
      setActiveTab(tab);
    }

    if (orderCode) {
      getListHistory(1, null, orderCode);
    } else {
      const defaultRange = rangePresets[2].value;
      getListHistory(1, defaultRange);
    }
  }, [location.search]);

  const onFinish = (values) => {
    setHasSearched(true);
    setCurrentPage(1);
    if (activeTab === "2") {
      if (!values.orderCode) return;
      getListHistory(1, null, values.orderCode);
    } else {
      if (!values.dateRange || values.dateRange.length < 2) return;
      getListHistory(1, values.dateRange);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (activeTab === "2") {
      getListHistory(page, null, form.getFieldValue("orderCode"));
    } else {
      getListHistory(page, form.getFieldValue("dateRange"));
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setListHistory([]);
    setTotal(0);
    setHasSearched(false);
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-300 space-y-3">
      <h2 className="font-bold text-lg text-gray-800 mb-4 px-1">
        Giao dịch gần đây
      </h2>

      <div className="bg-white p-4 rounded-2xl shadow-sm">
        <Form
          form={form}
          onFinish={onFinish}
          layout="vertical"
          initialValues={{
            dateRange: rangePresets[2].value,
          }}
          className="m-0 bg-white w-full"
        >
          <style>{`
            .custom-tabs .ant-tabs-nav-list {
              width: 100%;
            }
            .custom-tabs .ant-tabs-tab {
              width: 50%;
              justify-content: center;
            }
          `}</style>
          <Tabs
            className="custom-tabs"
            activeKey={activeTab}
            onChange={handleTabChange}
            items={[
              {
                key: "1",
                label: "Theo ngày",
                children: (
                  <div className="flex items-start gap-2 w-full mt-2">
                    <Form.Item
                      name={"dateRange"}
                      rules={[{ required: activeTab === "1", message: "Vui lòng chọn thời gian!" }]}
                      className="flex-1 m-0"
                    >
                      <DatePicker.RangePicker
                        popupClassName="ant-picker-no-left"
                        presets={rangePresets}
                        format="DD/MM/YYYY"
                        className="h-10 w-full"
                        locale={locale}
                        size="large"
                        suffixIcon={
                          <svg
                            className="text-primary"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10.8984 14.2188C10.8984 15.0803 11.5994 15.7812 12.4609 15.7812H14.2969C15.1584 15.7812 15.8594 15.0803 15.8594 14.2188V12.3828C15.8594 11.5213 15.1584 10.8203 14.2969 10.8203H12.4609C11.5994 10.8203 10.8984 11.5213 10.8984 12.3828V14.2188ZM12.4609 12.3828H14.2969L14.2979 14.2187C14.2979 14.2187 14.2976 14.2188 14.2969 14.2188H12.4609V12.3828Z"
                              fill="currentColor"
                            />
                            <path
                              d="M19.2188 13.75C19.6502 13.75 20 13.4002 20 12.9688V4.6875C20 2.96438 18.5981 1.5625 16.875 1.5625H15.8594V0.78125C15.8594 0.349766 15.5096 0 15.0781 0C14.6466 0 14.2969 0.349766 14.2969 0.78125V1.5625H10.7422V0.78125C10.7422 0.349766 10.3924 0 9.96094 0C9.52945 0 9.17969 0.349766 9.17969 0.78125V1.5625H5.66406V0.78125C5.66406 0.349766 5.3143 0 4.88281 0C4.45133 0 4.10156 0.349766 4.10156 0.78125V1.5625H3.125C1.40188 1.5625 0 2.96438 0 4.6875V16.875C0 18.5981 1.40188 20 3.125 20H16.875C18.5981 20 20 18.5981 20 16.875C20 16.4435 19.6502 16.0938 19.2188 16.0938C18.7873 16.0938 18.4375 16.4435 18.4375 16.875C18.4375 17.7366 17.7366 18.4375 16.875 18.4375H3.125C2.26344 18.4375 1.5625 17.7366 1.5625 16.875V4.6875C1.5625 3.82594 2.26344 3.125 3.125 3.125H4.10156V3.90625C4.10156 4.33773 4.45133 4.6875 4.88281 4.6875C5.3143 4.6875 5.66406 4.33773 5.66406 3.90625V3.125H9.17969V3.90625C9.17969 4.33773 9.52945 4.6875 9.96094 4.6875C10.3924 4.6875 10.7422 4.33773 10.7422 3.90625V3.125H14.2969V3.90625C14.2969 4.33773 14.6466 4.6875 15.0781 4.6875C15.5096 4.6875 15.8594 4.33773 15.8594 3.90625V3.125H16.875C17.7366 3.125 18.4375 3.82594 18.4375 4.6875V12.9688C18.4375 13.4002 18.7873 13.75 19.2188 13.75Z"
                              fill="currentColor"
                            />
                            <path
                              d="M4.88281 15.7812C5.31428 15.7812 5.66406 15.4315 5.66406 15C5.66406 14.5685 5.31428 14.2188 4.88281 14.2188C4.45134 14.2188 4.10156 14.5685 4.10156 15C4.10156 15.4315 4.45134 15.7812 4.88281 15.7812Z"
                              fill="currentColor"
                            />
                            <path
                              d="M4.88281 8.98438C5.31428 8.98438 5.66406 8.6346 5.66406 8.20312C5.66406 7.77165 5.31428 7.42188 4.88281 7.42188C4.45134 7.42188 4.10156 7.77165 4.10156 8.20312C4.10156 8.6346 4.45134 8.98438 4.88281 8.98438Z"
                              fill="currentColor"
                            />
                            <path
                              d="M4.88281 12.3828C5.31428 12.3828 5.66406 12.033 5.66406 11.6016C5.66406 11.1701 5.31428 10.8203 4.88281 10.8203C4.45134 10.8203 4.10156 11.1701 4.10156 11.6016C4.10156 12.033 4.45134 12.3828 4.88281 12.3828Z"
                              fill="currentColor"
                            />
                            <path
                              d="M8.28125 12.3828C8.71272 12.3828 9.0625 12.033 9.0625 11.6016C9.0625 11.1701 8.71272 10.8203 8.28125 10.8203C7.84978 10.8203 7.5 11.1701 7.5 11.6016C7.5 12.033 7.84978 12.3828 8.28125 12.3828Z"
                              fill="currentColor"
                            />
                            <path
                              d="M8.28125 8.98438C8.71272 8.98438 9.0625 8.6346 9.0625 8.20312C9.0625 7.77165 8.71272 7.42188 8.28125 7.42188C7.84978 7.42188 7.5 7.77165 7.5 8.20312C7.5 8.6346 7.84978 8.98438 8.28125 8.98438Z"
                              fill="currentColor"
                            />
                            <path
                              d="M8.28125 15.7812C8.71272 15.7812 9.0625 15.4315 9.0625 15C9.0625 14.5685 8.71272 14.2188 8.28125 14.2188C7.84978 14.2188 7.5 14.5685 7.5 15C7.5 15.4315 7.84978 15.7812 8.28125 15.7812Z"
                              fill="currentColor"
                            />
                          </svg>
                        }
                      />
                    </Form.Item>
                    <Form.Item className="m-0">
                      <Button
                        type="primary"
                        htmlType="submit"
                        size="large"
                        className="!bg-primary !border-primary h-10 font-bold"
                      >
                        Tìm kiếm
                      </Button>
                    </Form.Item>
                  </div>
                )
              },
              {
                key: "2",
                label: "Theo mã đơn hàng",
                children: (
                  <div className="flex items-start gap-2 w-full mt-2">
                    <Form.Item
                      name={"orderCode"}
                      rules={[{ required: activeTab === "2", message: "Vui lòng nhập mã đơn hàng!" }]}
                      className="flex-1 m-0"
                    >
                      <Input className="h-10 w-full" allowClear placeholder="Nhập mã đơn hàng..." />
                    </Form.Item>
                    <Form.Item className="m-0">
                      <Button
                        type="primary"
                        htmlType="submit"
                        size="large"
                        className="!bg-primary !border-primary h-10 font-bold"
                      >
                        Tìm kiếm
                      </Button>
                    </Form.Item>
                  </div>
                )
              }
            ]}
          />
        </Form>
      </div>

      {listHistory.length > 0 ? (
        <>
          {listHistory.map((item, index) => {
            const info = getServiceInfo(item);
            const isSuccess = item.paymentstatus === "Success";

            return (
              <div
                key={item.id || index}
                className="bg-white p-4 rounded-2xl shadow-sm flex items-center gap-3"
              >
                <div className={`p-3 rounded-2xl ${info.bg} ${info.color}`}>
                  <info.icon size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-800 text-sm truncate">
                    {info.label}
                    {item.quantity > 1 && (
                      <span className="ml-2 text-xs font-medium bg-primary-100 text-primary px-1.5 py-0.5 rounded inline-block">
                        x{item.quantity}
                      </span>
                    )}
                  </p>
                  {item.ordercode && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      Mã ĐH: <span className="font-medium text-gray-700 text-[10px] break-all">{item.ordercode}</span>
                    </p>
                  )}
                  {info.servicecode && (
                    <p className="text-xs text-gray-500 mt-0.5">{info.servicecode}</p>
                  )}
                  <p className="text-sm text-gray-400 mt-0.5 flex items-center gap-1">
                    <Clock size={10} />
                    {moment(item.createdat).isValid() ? moment(item.createdat).format("HH:mm DD/MM/YYYY") : ""}
                  </p>
                  {item.phonecontact && (
                    <p className="text-xs text-gray-500 mt-0.5">SĐT: {item.phonecontact}</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="font-bold text-gray-800">
                    {formatVND(item.amount)}
                  </p>
                  <p
                    className={`text-sm font-bold mt-1 py-0.5 px-2 rounded-full inline-block ${isSuccess
                      ? "text-green-600 bg-green-50"
                      : "text-red-500 bg-red-50"
                      }`}
                  >
                    {isSuccess ? "Thanh toán thành công" : "Thanh toán thất bại"}
                  </p>
                </div>
              </div>
            );
          })}

          <div className="flex justify-center pt-4 pb-2">
            <Pagination
              current={currentPage}
              pageSize={PAGE_SIZE}
              total={total}
              onChange={handlePageChange}
              showSizeChanger={false}
              size="small"
            />
          </div>
        </>
      ) : hasSearched ? (
        <div className="text-center py-12">
          <div className="w-20 h-20 mx-auto mb-4 text-gray-300 flex items-center justify-center">
            <svg
              width="80"
              height="80"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
              <path d="M3 3v5h5" />
            </svg>
          </div>
          <p className="text-gray-400 text-[14px]">
            Không có giao dịch nào.
          </p>
        </div>
      ) : null}
    </div>
  );
};

export default History;
